import { useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useCreateTask, useEmployees, useInfostaff, useMeetings,
  useMyCourses, useMyProfile, useShiftLocations, useShifts, useTasks, useToggleTask,
} from '../api/hooks';
import type { Shift, Task } from '../api/types';
import { useT } from '../i18n/LangProvider';
import { Icon } from '../ui/Icon';
import { TaskModal } from '../components/TaskModal';

// ── "Il mio turno" (right rail, as in the operations board) ──
const SHIFT_STATES: Record<string, string> = { off: 'OFF', standby: 'STAND-BY', ferie: 'FERIE', malattia: 'MALATTIA', permesso: 'PERMESSO' };
const LOC_COLOR: Record<string, string> = { uff: '#e79aa6', vat: '#93cf8f', col: '#5bb8e6', nav: '#f2a63d', home: '#c3ace0', csam: '#9ad0c0', borgh: '#e6c34a' };
const shiftIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function MyShiftCard({ onNav }: { onNav: (p: string) => void }) {
  const tr = useT();
  const auth = useAuth();
  const shiftsQ = useShifts();
  const locsQ = useShiftLocations();
  const locs = locsQ.data ?? [];
  const shortOf = (k?: string | null) => locs.find((l) => l.key === k)?.short ?? k?.toUpperCase() ?? '';
  const colorOf = (k?: string | null) => LOC_COLOR[k ?? ''] ?? '#c9c9c9';

  const mine: Record<string, Shift> = {};
  for (const s of shiftsQ.data ?? []) if (s.employeeId === auth.sub) mine[s.date.slice(0, 10)] = s;

  const days = Array.from({ length: 4 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i); return d; });
  const segsOf = (s?: Shift) => (s?.segments?.length ? s.segments : (s?.locationKey ? [{ loc: s.locationKey, range: s.range }] : [])).filter((x) => x?.loc);
  const today = mine[shiftIso(days[0])];
  const todayState = today?.typeKey && SHIFT_STATES[today.typeKey] ? SHIFT_STATES[today.typeKey] : null;
  const todaySegs = segsOf(today);

  return (
    <>
      <div className="hub-section-head">
        <h2>{tr('Il mio turno')}</h2>
        <a href="#" onClick={(e) => { e.preventDefault(); onNav('shifts'); }}>{tr('Vedi settimana →')}</a>
      </div>
      <div className="card" style={{ padding: 14 }}>
        <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700, marginBottom: 6 }}>{tr('Oggi')}</div>
        {todayState ? (
          <div style={{ background: '#ffe74d', color: '#5a4a00', fontWeight: 700, borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>{tr(todayState)}</div>
        ) : todaySegs.length > 0 ? (
          <div className="row" style={{ gap: 6 }}>
            {todaySegs.map((s, i) => (
              <span key={i} style={{ flex: 1, background: colorOf(s.loc), color: '#222', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 12.5 }}>{shortOf(s.loc)}</span>
                {s.range && <span style={{ fontSize: 11 }}>{s.range}</span>}
              </span>
            ))}
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 13 }}>{tr('— nessun turno assegnato —')}</div>
        )}

        <div className="row" style={{ gap: 6, marginTop: 12 }}>
          {days.slice(1).map((d) => {
            const s = mine[shiftIso(d)];
            const st = s?.typeKey && SHIFT_STATES[s.typeKey] ? SHIFT_STATES[s.typeKey] : null;
            const segs = segsOf(s);
            return (
              <div key={shiftIso(d)} style={{ flex: 1, textAlign: 'center' }}>
                <div className="muted" style={{ fontSize: 11, marginBottom: 3 }}>
                  {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'][d.getDay()]} {d.getDate()}
                </div>
                {st ? (
                  <div style={{ background: '#ffe74d', color: '#5a4a00', fontWeight: 700, fontSize: 10.5, borderRadius: 6, padding: '4px 2px' }}>{tr(st)}</div>
                ) : segs.length > 0 ? (
                  <div style={{ background: colorOf(segs[0].loc), color: '#222', fontWeight: 700, fontSize: 10.5, borderRadius: 6, padding: '4px 2px' }}>
                    {shortOf(segs[0].loc)}{segs.length > 1 ? `/${shortOf(segs[1].loc)}` : ''}
                  </div>
                ) : (
                  <div className="muted" style={{ fontSize: 12, border: '1px dashed var(--line,#ddd)', borderRadius: 6, padding: '3px 2px' }}>—</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ── date helpers ──
const parseISO = (s: string | null) => (s ? new Date(s) : null);
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const isToday = (iso: string | null) => {
  const d = parseISO(iso);
  return !!d && startOfDay(d).getTime() === startOfDay(new Date()).getTime();
};
const isOverdue = (iso: string | null) => !!iso && parseISO(iso)!.getTime() < Date.now();

function formatDueLabel(iso: string | null, tr: (s: string) => string): string | null {
  if (!iso) return null;
  const d = parseISO(iso)!;
  const diff = Math.round((startOfDay(d).getTime() - startOfDay(new Date()).getTime()) / 86400000);
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const hasTime = !(d.getHours() === 0 && d.getMinutes() === 0);
  if (diff === 0) return hasTime ? `${tr('Oggi')} · ${time}` : tr('Oggi');
  if (diff === 1) return hasTime ? `${tr('Domani')} · ${time}` : tr('Domani');
  if (diff === -1) return tr('Ieri');
  return `${d.getDate()}/${d.getMonth() + 1}${hasTime ? ` · ${time}` : ''}`;
}

const FILTERS = [
  { id: 'aperte', label: 'Aperte', short: 'Aperte' },
  { id: 'alta', label: 'Alta priorità', short: 'Alta' },
  { id: 'inritardo', label: 'In ritardo', short: 'In ritardo' },
  { id: 'completate', label: 'Completate', short: 'Fatte' },
  { id: 'tutte', label: 'Tutte', short: 'Tutte' },
] as const;
type FilterId = (typeof FILTERS)[number]['id'];

function TaskRow({ t, onToggle, onOpen }: { t: Task; onToggle: () => void; onOpen: () => void }) {
  const tr = useT();
  const overdue = !t.done && isOverdue(t.dueAt) && !isToday(t.dueAt);
  const dueLbl = formatDueLabel(t.dueAt, tr);
  return (
    <div className={`task-row ${t.done ? 'is-done' : ''} ${overdue ? 'is-overdue' : ''}`} style={{ cursor: 'pointer' }} onClick={onOpen}>
      <button
        className={`checkbox ${t.done ? 'is-on' : ''}`}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        aria-label={t.done ? tr('Segna come da fare') : tr('Segna come fatta')}
      >
        {t.done && <Icon name="check" size={11} stroke={3} />}
      </button>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className={`task-row__title ${t.done ? 'is-done' : ''}`}>{t.title}</div>
        <div className="task-row__meta">
          {t.priority && (
            <span className={`task-pri task-pri--${t.priority}`}>
              <span className="task-pri__dot" />{tr(t.priority)}
            </span>
          )}
          {dueLbl && (
            <span className={`task-due ${overdue ? 'is-overdue' : ''} ${isToday(t.dueAt) ? 'is-today' : ''}`}>
              <Icon name="clock" size={11} /> {dueLbl}
            </span>
          )}
          {t.tag && <span className="task-tag">#{t.tag}</span>}
        </div>
      </div>
    </div>
  );
}

export function HubScreen({ onNav }: { onNav: (p: string) => void }) {
  const tr = useT();
  const auth = useAuth();
  const tasksQ = useTasks();
  const infostaffQ = useInfostaff();
  const profileQ = useMyProfile();
  const myCoursesQ = useMyCourses();
  const meetingsQ = useMeetings();
  const employeesQ = useEmployees();
  const toggleTask = useToggleTask();
  const createTask = useCreateTask();

  const [filter, setFilter] = useState<FilterId>('aperte');
  const [newTitle, setNewTitle] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const tasks = tasksQ.data?.items ?? [];
  const employees = employeesQ.data?.items ?? [];
  const openTask = tasks.find((t) => t.id === openId) ?? null;
  const counts = {
    aperte: tasks.filter((t) => !t.done).length,
    alta: tasks.filter((t) => !t.done && t.priority === 'alta').length,
    inritardo: tasks.filter((t) => !t.done && isOverdue(t.dueAt)).length,
    completate: tasks.filter((t) => t.done).length,
    tutte: tasks.length,
  };
  const visibleTasks = useMemo(() => {
    let list = [...tasks];
    if (filter === 'aperte') list = list.filter((t) => !t.done);
    else if (filter === 'alta') list = list.filter((t) => !t.done && t.priority === 'alta');
    else if (filter === 'inritardo') list = list.filter((t) => !t.done && isOverdue(t.dueAt));
    else if (filter === 'completate') list = list.filter((t) => t.done);
    return list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [tasks, filter]);

  const todayTasks = tasks.filter((t) => isToday(t.dueAt) || (t.done && isToday(t.doneAt)));
  const todayDone = todayTasks.filter((t) => t.done).length;
  const todayTotal = todayTasks.length;
  const todayPct = todayTotal === 0 ? 0 : Math.round((todayDone / todayTotal) * 100);
  const overdueCount = tasks.filter((t) => !t.done && isOverdue(t.dueAt) && !isToday(t.dueAt)).length;

  // Mandatory courses still to complete — surfaced up top so they cannot be missed.
  const mandatoryDue = (myCoursesQ.data ?? [])
    .filter((a) => a.course.mandatory && a.status !== 'completed')
    .map((a) => ({
      id: a.courseId,
      title: a.course.title,
      progress: a.progress,
      overdue: !!a.dueAt && new Date(a.dueAt).getTime() < Date.now(),
      dueAt: a.dueAt,
    }));
  const mandatoryOverdue = mandatoryDue.filter((c) => c.overdue).length;

  const unreadInfo = infostaffQ.data?.items.filter((i) => !i.read).length ?? 0;
  const recentInfo = (infostaffQ.data?.items ?? []).filter((i) => !i.read).slice(0, 3);
  // Upcoming events only, soonest first — like a mini agenda.
  const EVENT_COLORS: Record<string, string> = { orange: 'var(--t-orange)', magenta: 'var(--t-magenta)', purple: 'var(--t-purple)', info: 'var(--info,#2b7bb9)' };
  const todayKey = new Date(); todayKey.setHours(0, 0, 0, 0);
  const nextMeetings = (meetingsQ.data?.items ?? [])
    .filter((m) => new Date(`${m.date.slice(0, 10)}T${(m.end || m.start || '23:59')}:00`) >= todayKey)
    .sort((a, b) => (a.date.slice(0, 10) + a.start).localeCompare(b.date.slice(0, 10) + b.start))
    .slice(0, 5);
  const eventDayLabel = (iso: string) => {
    const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
    const diff = Math.round((d.getTime() - todayKey.getTime()) / 86400000);
    if (diff === 0) return tr('Oggi');
    if (diff === 1) return tr('Domani');
    return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  // The nickname is a private personalisation: only the owner ever sees it.
  const firstName = profileQ.data?.nickname?.trim()
    || (auth.name || auth.email).split(' ')[0].split('@')[0];
  const hour = new Date().getHours();
  const greeting = hour < 13 ? 'Buongiorno' : hour < 18 ? 'Buon pomeriggio' : 'Buonasera';
  const today = new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });

  const addTask = (e: FormEvent) => {
    e.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    // Tasks created from the personal Hub default to the current user (token sub = employee id).
    createTask.mutate({ title, priority: 'media', assigneeId: auth.sub || undefined });
    setNewTitle('');
  };

  const avatarUrl = profileQ.data?.avatarUrl ?? null;
  const avatarInitials = ((auth.name || auth.email).trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2) || (auth.email[0] ?? '?')).toUpperCase();

  return (
    <div className="content hub-content">
      <div className="hero">
        <div className="row" style={{ gap: 16, alignItems: 'center', minWidth: 0 }}>
          <div className="hero__avatar" aria-hidden="true">
            {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>{avatarInitials}</span>}
          </div>
          <div>
            <div className="hero__greeting">{tr(greeting)}, {firstName}.</div>
            <div className="hero__sub" style={{ textTransform: 'capitalize' }}>{today}</div>
          </div>
        </div>
        <div className="hero__quick">
          <div className="hero__stat">
            <div className="hero__stat-label">{tr('Task oggi')}</div>
            <div className="hero__stat-value">
              {todayDone}<span style={{ color: 'var(--ink-4)', fontSize: 16, fontWeight: 500 }}>/{todayTotal}</span>
            </div>
            <div className="hero__progress" aria-hidden="true">
              <div className="hero__progress-bar" style={{ width: `${todayPct}%` }} />
            </div>
          </div>
          <div style={{ width: 1, height: 56, background: 'var(--line)' }} />
          <div className="hero__stat">
            <div className="hero__stat-label">{tr('In ritardo')}</div>
            <div className="hero__stat-value" style={{ color: overdueCount ? 'var(--danger)' : 'var(--ink)' }}>{overdueCount}</div>
            <div className="hero__stat-hint">{overdueCount === 0 ? tr('tutto in linea') : tr('da recuperare')}</div>
          </div>
          <div style={{ width: 1, height: 56, background: 'var(--line)' }} />
          <div className="hero__stat">
            <div className="hero__stat-label">{tr('Infostaff da leggere')}</div>
            <div className="hero__stat-value" style={{ color: unreadInfo ? 'var(--t-orange)' : 'var(--ink)' }}>{unreadInfo}</div>
            <div className="hero__stat-hint">{unreadInfo === 0 ? tr('nessuna nuova') : tr('comunicazioni nuove')}</div>
          </div>
        </div>
      </div>

      {mandatoryDue.length > 0 && (
        <div className={`mandatory-banner ${mandatoryOverdue ? 'is-overdue' : ''}`}>
          <div className="mandatory-banner__icon"><Icon name="shield" size={20} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mandatory-banner__title">
              {mandatoryDue.length === 1
                ? tr('Hai un corso obbligatorio da completare')
                : tr('Hai {n} corsi obbligatori da completare', { n: mandatoryDue.length })}
              {mandatoryOverdue > 0 && <span className="mandatory-banner__flag">{tr('{n} in ritardo', { n: mandatoryOverdue })}</span>}
            </div>
            <div className="mandatory-banner__list">
              {mandatoryDue.slice(0, 3).map((c) => (
                <button key={c.id} className="mandatory-banner__chip" onClick={() => onNav('learning')}>
                  <Icon name="cap" size={12} /> {c.title}
                  {c.progress > 0 && <span className="mandatory-banner__pct">{c.progress}%</span>}
                  {c.overdue && c.dueAt && <span className="mandatory-banner__due">{tr('scaduto')}</span>}
                </button>
              ))}
              {mandatoryDue.length > 3 && <span className="muted" style={{ fontSize: 12.5 }}>{tr('+{n} altri', { n: mandatoryDue.length - 3 })}</span>}
            </div>
          </div>
          <button className="btn btn--brand" onClick={() => onNav('learning')}>
            <Icon name="play" size={13} /> {tr('Vai alla formazione')}
          </button>
        </div>
      )}

      <div className="grid grid-3 hub-grid">
        <div className="hub-task-panel">
          <div className="hub-section-head">
            <h2>{tr('Le tue task')}</h2>
            <span className="muted hub-section-hint" style={{ fontSize: 12.5 }}>{tr('Invio per aggiungere · clicca per aprire')}</span>
          </div>

          <div className="card task-card">
            <form onSubmit={addTask} className="task-quickadd">
              <button type="button" className="checkbox" style={{ marginRight: 12, cursor: 'default', opacity: 0.5 }} />
              <input
                className="input task-quickadd__input"
                placeholder={tr('Aggiungi una task… (Invio per salvare)')}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
              {newTitle && <button type="submit" className="btn btn--sm btn--brand">{tr('Aggiungi')}</button>}
            </form>

            <div className="task-toolbar">
              <div className="task-filters" role="tablist">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    role="tab"
                    aria-selected={filter === f.id}
                    className={`task-filter ${filter === f.id ? 'is-active' : ''} ${f.id === 'inritardo' && counts.inritardo > 0 ? 'has-warn' : ''}`}
                    onClick={() => setFilter(f.id)}
                  >
                    <span className="task-filter__label task-filter__label--full">{tr(f.label)}</span>
                    <span className="task-filter__label task-filter__label--short">{tr(f.short)}</span>
                    {counts[f.id] > 0 && <span className="task-filter__count">{counts[f.id]}</span>}
                  </button>
                ))}
              </div>
            </div>

            {visibleTasks.length === 0 ? (
              <div className="task-empty">
                <Icon name="check" size={28} />
                <div className="task-empty__title">
                  {filter === 'completate' ? tr('Nessuna task completata')
                    : filter === 'inritardo' ? tr('Nessuna task in ritardo, ottimo!')
                    : filter === 'alta' ? tr('Nessuna task ad alta priorità')
                    : tr('Nessuna task — aggiungine una qui sopra per iniziare.')}
                </div>
              </div>
            ) : (
              visibleTasks.map((t) => (
                <TaskRow key={t.id} t={t} onToggle={() => toggleTask.mutate(t)} onOpen={() => setOpenId(t.id)} />
              ))
            )}
          </div>
        </div>

        <div>
          <MyShiftCard onNav={onNav} />

          <div className="hub-section-head" style={{ marginTop: 22 }}>
            <h2>{tr('Prossimi appuntamenti')}</h2>
            <a href="#" onClick={(e) => { e.preventDefault(); onNav('calendar'); }}>{tr('Calendario →')}</a>
          </div>
          <div className="card">
            {nextMeetings.length === 0 && <div className="card__body muted">{tr('Nessun appuntamento in programma.')}</div>}
            {nextMeetings.map((m) => (
              <div key={m.id} className="infostaff-item" onClick={() => onNav('calendar')}>
                <div className="infostaff-item__rail" style={{ background: EVENT_COLORS[m.color ?? ''] ?? 'var(--t-purple)' }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="infostaff-item__title">{m.title}</div>
                  <div className="infostaff-item__meta">
                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{eventDayLabel(m.date)}</span>
                    <span>· {m.start}{m.end ? `–${m.end}` : ''}</span>
                    {m.location && <span className="muted">· {m.location}</span>}
                  </div>
                </div>
                <Icon name="chevR" size={14} />
              </div>
            ))}
          </div>

          <div className="hub-section-head" style={{ marginTop: 22 }}>
            <h2>{tr('Infostaff recenti')}</h2>
            <a href="#" onClick={(e) => { e.preventDefault(); onNav('infostaff'); }}>{tr('Tutte →')}</a>
          </div>
          <div className="card">
            {recentInfo.length === 0 && <div className="card__body muted">{tr('Nessuna nuova comunicazione.')}</div>}
            {recentInfo.map((i) => (
              <div key={i.id} className={`infostaff-item is-unread ${i.urgent ? 'is-urgent' : ''}`} onClick={() => onNav('infostaff')}>
                <div className="infostaff-item__rail" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="infostaff-item__title">{i.title}</div>
                  <div className="infostaff-item__meta">
                    {i.category && <span className={`badge is-${i.urgent ? 'urgent' : 'info'}`}>{tr(i.category)}</span>}
                    <span>{new Date(i.publishedAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</span>
                  </div>
                </div>
                <Icon name="chevR" size={14} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {openTask && <TaskModal task={openTask} employees={employees} onClose={() => setOpenId(null)} />}
    </div>
  );
}
